import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators, FormGroupDirective } from '@angular/forms';
import { LookupGroup, valueGroupOptions } from '../../../shared/autocomplete';
import { ReplaySubject, Observable, of, combineLatest, Subscription } from 'rxjs';
import { ILookupValue } from '../../models/lookupvalues';
import { CalculationService } from '../../service/calculation.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PopupService } from '../../service/popup.service';
import { MessageService } from '../../service/message.service';
import { first, flatMap, map } from 'rxjs/operators';
import { toggleEditMode } from '../../../shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { LookupService } from '../../service/lookup.service';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { IVorversicherung } from '../../models/vorversicherung';
import { ErrorMessageType } from '../../../shared/enum';

@Component({
  selector: 'app-vorversicherung',
  templateUrl: './vorversicherung.component.html',
  styleUrls: ['./vorversicherung.component.scss']
})
export class VorversicherungComponent implements OnInit, OnChanges {

  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  showGroup = true;

  @Input()
  isKfz = false;

  @Input()
  expertMode: boolean;

  versicherungGroupOptions: Observable<LookupGroup[]>;
  oesterreichischeVu = new ReplaySubject<Array<ILookupValue>>();
  autocompleteTriggerSubscription: Subscription;

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

  constructor(private calculationService: CalculationService, private popupService: PopupService, private ref: ChangeDetectorRef, public translateService: TranslateService,
    public lookupService: LookupService, private messageService: MessageService) { }

  static createFormGroup(fb: UntypedFormBuilder) {
    return fb.group({
      vorversicherungsUnternehmen: new UntypedFormControl('', Validators.required),
      sonstigeVersicherung: new UntypedFormControl(''),
      polizzennummerVorversicherung: new UntypedFormControl('')
    });
  }

  ngOnInit(): void {
    this.loadOesterreichischeVu();
    this.toggleEditMode();
    this.onValueChanged();
    this.createVersicherungAutocomplete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.showGroup && changes.showGroup.currentValue !== changes.showGroup.previousValue) {
      this.toggleEditMode();
      this.onAutocompletePanelClosed();
      this.ref.detectChanges();
    }
  }

  get vorversicherungsUnternehmen() { return this.formGroup.get('vorversicherungsUnternehmen'); }
  get sonstigeVersicherung() { return this.formGroup.get('sonstigeVersicherung'); }
  get polizzennummerVorversicherung() { return this.formGroup.get('polizzennummerVorversicherung'); }

  get showSonstigeVersicherung() {
    return this.vorversicherungsUnternehmen.value?.Key === '999' ? true : false;
  }

  loadOesterreichischeVu(): void {
    this.calculationService.getOesterreichischeVu().subscribe(
      data => {
        this.oesterreichischeVu.next(data.OesterreichischeVu);
      },
      (err: HttpErrorResponse) => {
        this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.General));
        console.log(err.message);
      }
    );
  }

  toggleEditMode(): void {
    toggleEditMode(this.vorversicherungsUnternehmen, this.showGroup);
    toggleEditMode(this.sonstigeVersicherung, this.showSonstigeVersicherung);
    toggleEditMode(this.polizzennummerVorversicherung, this.showGroup);
  }

  onValueChanged(): void {
    this.vorversicherungsUnternehmen.valueChanges.subscribe(() => {
      this.toggleEditMode();
    });
  }

  createVersicherungAutocomplete(): void {
    const oesterreichischeVuGroupOptions = valueGroupOptions(this.oesterreichischeVu.pipe(first(), flatMap(res => res),
      map(res => ({ category: 'Versicherungen', value: res }))), this.vorversicherungsUnternehmen);
    const versicherungSonstigesGroupOptions = of([
      {
        letter: 'Sonstiges',
        values: [
          { Key: '998', Id: '998', Value: 'Versicherung im Ausland' },
          { Key: '999', Id: '999', Value: 'Sonstige Versicherung (bitte eingeben)' },
        ],
      },
    ]);
    this.versicherungGroupOptions = combineLatest([
      oesterreichischeVuGroupOptions,
      versicherungSonstigesGroupOptions
    ]).pipe(
      map(([versicherungenGroupOptions, sonstigesGroupOptions]) => [...versicherungenGroupOptions, ...sonstigesGroupOptions])
    );
  }

  onAutocompletePanelClosed(): void {
    if (this.showGroup === true) {
      if (this.autocompleteTriggerSubscription) {
        this.autocompleteTriggerSubscription.unsubscribe();
      }
      this.ref.detectChanges();
      this.autocompleteTriggerSubscription = this.autocompleteTrigger?.panelClosingActions
        .subscribe(res => {
          if (!(res && res.source)) {
            this.vorversicherungsUnternehmen.setValue(null);
            this.autocompleteTrigger.closePanel();
          }
        });
    }
  }

  setVorversicherung(vorversicherung: IVorversicherung) {
    this.versicherungGroupOptions.pipe(first()).toPromise().then((vorversicherungen) => {
      this.vorversicherungsUnternehmen.setValue([].concat(...vorversicherungen.map(x => x.values)).find(x => x.Id === vorversicherung.VorversicherungsUnternehmenId.toString()));
      this.sonstigeVersicherung.setValue(vorversicherung.SonstigeVersicherung);
      this.polizzennummerVorversicherung.setValue(vorversicherung.PolizzennummerVorversicherung);
    });
  }

  getVorversicherung(): IVorversicherung {
    return this.showGroup && this.vorversicherungsUnternehmen.value ? {
      VorversicherungsUnternehmenId: Number(this.vorversicherungsUnternehmen.value.Id),
      SonstigeVersicherung: this.sonstigeVersicherung.value,
      PolizzennummerVorversicherung: this.polizzennummerVorversicherung.value
    } : null;
  }
}
